// @flow
import * as React from 'react';
import type { Element } from 'react';
import { connect } from 'react-redux';
import type { GlobalState } from '@globalTypes';
import { useTranslation, Trans } from '@i18n';
import UploadNew from '@components/UploadNew/index';
import { useState } from 'react';
import { CrossIcon, UploadTick } from '@images/svg/index';
import {
  StyledUploadWrapper,
  StyledShowDocumentsWrapper,
  StyledFileWrapper,
  StyledFileNameWrapper,
  StyledIconWrapper,
  StyledButtonSubmit
} from './styles';

import { newPatientCreateDocument, createDocument } from './redux/actions';

import type { Props } from './types';

const DocumentsUploadNew = ({
  documentsUpload,
  newPatientCreateDocument,
  opinionFlow,
  created_patient_id,
  onTimeSelect,
  setSuccess,
  doctorUpload,
  setModalSuccess,
  treatmentFormName,
  isModal,
  TreatmentCostFlow
}: Props): Element<any> => {
  const { t } = useTranslation('Profile');
  const [documentsArr, setDocumentArr] = useState<any>([]);
  const [uploadCount, setUploadCount] = useState(0);
  const [finish, setFinish] = useState(false);
  const handleDelete = (key) => {
    const newarray = documentsArr.filter(obj => obj.uploadCount !== key);

    setDocumentArr([...newarray]);
  };

  const queueState = documentsUpload.createStatus.queueInProgress > 0;

  React.useEffect(() => {
    if (finish) handleFinish();
  }, [queueState]);

  const handleFinish = () => {
    if (documentsUpload.createStatus.queueInProgress === 0) {
      if (onTimeSelect) {
        onTimeSelect();
      }
      if (setSuccess) {
        setSuccess(true);
      }

      if (setModalSuccess) {
        setModalSuccess(true);
      }
    }
  };

  const handleUpload = () => {
    if (documentsArr.length > 0) {
      newPatientCreateDocument(documentsArr);
    } else {
      handleFinish();
    }
  };

  const handleLoad = (reader, file, index) => {
    const currentUploadCount = uploadCount + index + 1;
    setUploadCount(currentUploadCount);

    setDocumentArr(prevArr => [
      ...prevArr,
      {
        file: reader.result,
        name: file.name,
        patient_id: created_patient_id,
        kind: 'medical_reports',
        uploadCount: currentUploadCount
      }
    ]);
  };

  const createInProgress = documentsUpload.createStatus.queueInProgress > 0;

  const description = <Trans i18nKey="Profile:Documents.Upload.DragDrop" />;

  const createText = createInProgress
    ? `Upload of ${documentsArr.length} file(s).`
    : description;

  const isError = documentsUpload.createStatus.queueError > 0;
  const errorText = t('Documents.Upload.IsError', {
    count: documentsUpload.createStatus.queueError
  });

  return (
    <>
      <StyledUploadWrapper treatmentFormName={treatmentFormName}>
        <UploadNew
          pending={createInProgress}
          buttonLabel="Upload"
          uploadLabel={isError ? errorText : createText}
          onLoad={handleLoad}
          isModal={isModal}
          treatmentFormName={treatmentFormName}
        />
      </StyledUploadWrapper>
      {documentsArr.length !== 0 && (
        <StyledShowDocumentsWrapper isModal={isModal}>
          {documentsArr.map(obj => (
            <StyledFileWrapper
              isModal={isModal}
              key={obj.uploadCount}
              onClick={() => handleDelete(obj.uploadCount)}
            >
              <StyledIconWrapper>
                <UploadTick />
              </StyledIconWrapper>
              <StyledFileNameWrapper isModal={isModal}>{obj.name}</StyledFileNameWrapper>
              <StyledIconWrapper>
                <CrossIcon />
              </StyledIconWrapper>
            </StyledFileWrapper>
          ))}
        </StyledShowDocumentsWrapper>
      )}
      <StyledButtonSubmit
        type="button"
        opinionFlow
        treatmentFormName={treatmentFormName}
        disabled={opinionFlow && documentsArr.length === 0}
        isModal={isModal}
        onClick={() => {
          setFinish(true);
          handleUpload();
        }}
        TreatmentCostFlow={TreatmentCostFlow}
      >
        {opinionFlow ? `Upload & ${doctorUpload || 'Book'} ` : t('Finish') }

      </StyledButtonSubmit>
    </>
  );
};

DocumentsUploadNew.getInitialProps = async () => ({
  namespacesRequired: ['Profile']
});

const mapStateToProps = (state: GlobalState): {} => ({
  documentsUpload: state.documentsUpload,
  patientId: state.personalData?.data?.id
});

export default connect(
  mapStateToProps,
  { createDocument, newPatientCreateDocument }
)(DocumentsUploadNew);
